/**
 * @fileoverview Pure domain rules for the KPI grid: what a row looks like,
 * when a cell counts as changed, what makes a row invalid, and the exact body
 * saveReportingQuestion expects. Every rule the grid enforces has its single
 * definition here — no call site is allowed a second opinion.
 *
 * A row's unsaved edits live in a `patch` object that holds only the fields
 * whose value differs from the saved row, which makes "is this cell dirty?"
 * a key-presence check rather than a comparison spread across components.
 *
 * Two columns arrive in more than one shape and are normalised here:
 *
 * `tags` is a list of paths, each path a run of `{ key, value }` pairs. It is
 * stored two ways in production — as an OBJECT of named paths (1,151 rows,
 * keys like `scope` / `energy` / `emission`) and as an ARRAY of unnamed paths
 * (9,544 rows). Both become the same in-memory list; the shape a row arrived
 * in is remembered on the row and written back unchanged, so editing a tag
 * never silently migrates 9,544 rows to the other form.
 *
 * `supported_units` is always `[{ unit_id, factor, is_base }]` — a conversion
 * table into the base unit, not a list of unit names.
 */

import {
  AGGREGATED_TYPES,
  EVIDENCE_KINDS,
  FACTOR_GASES,
  FIXED_TAG_GROUPS,
  KPI_TYPES,
  TABLE_QUESTION_TYPES,
  TAG_FIELDS,
} from "./options";

const DRAFT_PREFIX = "draft-";

let draftCounter = 0;

/**
 * @param {string} raw
 * @param {*} fallback
 */
export const safeParseJSON = (raw, fallback = []) => {
  if (raw == null) return fallback;
  if (typeof raw !== "string") return raw;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return fallback;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return fallback;
  }
};

const jsonList = (raw) => {
  const parsed = safeParseJSON(raw, []);
  return Array.isArray(parsed) ? parsed : [];
};

const stringList = (raw) => jsonList(raw).map(String);

/**
 * kpi_type is a JSON array such as `["EMISSION","ENERGY"]`; a lone type stored
 * as a bare string is read as a list of one. Types keep their stored spelling,
 * so one the company side does not match, such as `"Energy"`, stays visible.
 * @param {string|string[]|null} raw
 * @returns {string[]}
 */
const kpiTypesFromWire = (raw) => {
  const parsed = typeof raw === "string" ? safeParseJSON(raw, raw.trim()) : raw;
  const types = Array.isArray(parsed) ? parsed.map(String) : typeof parsed === "string" && parsed && parsed !== "null" ? [parsed] : [];
  return [...new Set(types)];
};

/** @param {string} type @returns {boolean} whether the company side matches this exact spelling */
export const isKnownKpiType = (type) => KPI_TYPES.some((option) => option.value === type);

/**
 * The KPI type options for a row: the five types, then each stored spelling
 * the company side does not match, labelled as such so it can be unticked.
 * @param {string[]} storedTypes
 * @returns {{ value: string, label: string }[]}
 */
export const kpiTypeOptionsFor = (storedTypes) => {
  const unknown = storedTypes.filter((type) => !isKnownKpiType(type));
  if (!unknown.length) return KPI_TYPES;
  return [...KPI_TYPES, ...unknown.map((type) => ({ value: type, label: `${type} (not recognised)` }))];
};

export const KPI_SHARED_FIELDS = ["title", "moduleId", "aggregation", "factors", "defaultDescription"];

/** @param {string|number} id */
export const isDraftId = (id) => String(id).startsWith(DRAFT_PREFIX);

export const nextDraftId = () => `${DRAFT_PREFIX}${++draftCounter}`;

/**
 * The key the backend derives on insert, shown greyed in the title cell so a
 * new row's identity is visible before it is saved.
 * @param {string} title
 */
export const kpiKeyFromTitle = (title) =>
  String(title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);

/** @param {string} type */
export const supportsAggregation = (type) => AGGREGATED_TYPES.includes(type);

/** @param {string} type @returns {boolean} whether the KPI is answered in a table built from `details` */
export const hasTableSetup = (type) => TABLE_QUESTION_TYPES.includes(type);

/**
 * Wire tags into a list of paths. An object becomes named paths, an array
 * becomes unnamed ones, and a flat array of pairs — the shape a hand-written
 * row might use — becomes a single unnamed path.
 *
 * @param {object|Array|null} wire
 * @returns {{ name: string|null, pairs: { key: string, value: string }[] }[]}
 */
export const tagGroupsFromWire = (wire) => {
  const raw = safeParseJSON(wire, null);
  if (!raw) return [];

  if (Array.isArray(raw)) {
    if (raw.length && !Array.isArray(raw[0]) && raw[0]?.key !== undefined) {
      return [{ name: null, pairs: raw.map(toPair) }];
    }
    return raw
      .filter(Array.isArray)
      .map((pairs) => ({ name: null, pairs: pairs.map(toPair) }));
  }

  return Object.keys(raw).map((name) => ({
    name,
    pairs: (Array.isArray(raw[name]) ? raw[name] : []).map(toPair),
  }));
};

const toPair = (pair) => ({ key: String(pair?.key ?? ""), value: String(pair?.value ?? "") });

/**
 * Which of the two storable shapes a row's tags came in. A row that had no
 * tags gets the object form, which is the one the newer screens write.
 * @param {object|Array|null} wire
 * @returns {"object"|"array"}
 */
export const tagShapeOfWire = (wire) => (Array.isArray(safeParseJSON(wire, null)) ? "array" : "object");

/**
 * Paths back to the wire, in the shape the row arrived in. Unnamed paths in a
 * row stored as an object are given positional names so nothing is dropped.
 *
 * @param {{ name: string|null, pairs: object[] }[]} groups
 * @param {"object"|"array"} shape
 */
export const tagGroupsToWire = (groups, shape) => {
  const kept = groups
    .map((group) => ({
      ...group,
      pairs: isFixedTagGroup(group) ? trimTrailingBlank(group.pairs) : group.pairs.filter(hasValue),
    }))
    .filter((group) => group.pairs.length > 0);
  if (!kept.length) return shape === "array" ? [] : {};

  if (shape === "array") return kept.map((group) => group.pairs.map(toPair));

  const out = {};
  kept.forEach((group, index) => {
    const name = group.name || `group${index + 1}`;
    out[name] = group.pairs.map(toPair);
  });
  return out;
};

const hasValue = (pair) => String(pair.value ?? "").trim() !== "";

const trimTrailingBlank = (pairs) => {
  let end = pairs.length;
  while (end > 0 && !hasValue(pairs[end - 1])) end -= 1;
  return pairs.slice(0, end);
};

const FIXED_TAG_NAMES = FIXED_TAG_GROUPS.map((group) => group.name);

/**
 * riu_keywords.keyword_type is free text — "category", "Category" and
 * "Sub Category" all occur — so a tag field finds its pairs ignoring case,
 * spaces and underscores.
 * @param {string} text
 */
const keywordKey = (text) => String(text ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The stored keyword_type a tag field belongs to, or the field itself when
 * nothing is stored under it yet — which is what a new pair is filed as.
 * @param {Record<string, string[]>} tagValuesByKey
 * @param {string} field e.g. "subCategory"
 */
export const keywordTypeFor = (tagValuesByKey, field) =>
  Object.keys(tagValuesByKey).find((type) => keywordKey(type) === keywordKey(field)) ?? field;

/**
 * Values already paired with a tag field's key.
 * @param {Record<string, string[]>} tagValuesByKey
 * @param {string} field
 */
export const keywordValuesFor = (tagValuesByKey, field) =>
  tagValuesByKey[keywordTypeFor(tagValuesByKey, field)] || [];

/** @param {{ name: string|null }} group */
export const isFixedTagGroup = (group) => FIXED_TAG_NAMES.includes(group.name);

/**
 * Writes one fixed-group cell. Missing positions before it are filled with
 * blank pairs so the value lands at the index the backend reads; an existing
 * pair keeps its key and only its value changes.
 *
 * @param {{ name: string|null, pairs: object[] }[]} groups
 * @param {string} groupName
 * @param {number} index position in TAG_FIELDS
 * @param {string} value
 */
export const setTagField = (groups, groupName, index, value) => {
  const at = groups.findIndex((group) => group.name === groupName);
  const pairs = at === -1 ? [] : [...groups[at].pairs];
  for (let i = pairs.length; i <= index; i += 1) pairs.push({ key: TAG_FIELDS[i]?.key ?? "", value: "" });
  pairs[index] = { ...pairs[index], value };
  if (at === -1) return [...groups, { name: groupName, pairs }];
  const next = [...groups];
  next[at] = { ...groups[at], pairs };
  return next;
};

/**
 * Every distinct `{ group, key, value }` across the loaded rows, for the
 * selection bar's "add this tag to the selected rows" control. Fixed groups
 * are left out: appending to them would shift the positions the backend reads.
 * @param {object[]} rowValues merged rows
 */
export const tagTriplesInUse = (rowValues) => {
  const seen = new Map();
  rowValues.forEach((values) => {
    (values.tagGroups || []).filter((group) => !isFixedTagGroup(group)).forEach((group) => {
      group.pairs.forEach((pair) => {
        const id = `${group.name ?? ""}|${pair.key}|${pair.value}`;
        if (!seen.has(id)) {
          seen.set(id, {
            id,
            group: group.name,
            key: pair.key,
            value: pair.value,
            label: `${group.name ? `${group.name} · ` : ""}${pair.key}: ${pair.value}`,
          });
        }
      });
    });
  });
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * The evidence picker's list: the standard kinds, then every other kind the
 * loaded KPIs already ask for, A–Z.
 * @param {{ evidences: string[] }[]} rows
 * @returns {string[]}
 */
export const evidenceKindsInUse = (rows) => {
  const standard = new Set(EVIDENCE_KINDS.map((kind) => kind.toLowerCase()));
  const extra = new Map();
  rows.forEach((row) =>
    (row.evidences || []).forEach((kind) => {
      const key = kind.trim().toLowerCase();
      if (key && !standard.has(key) && !extra.has(key)) extra.set(key, kind.trim());
    }),
  );
  return [...EVIDENCE_KINDS, ...[...extra.values()].sort((a, b) => a.localeCompare(b))];
};

/**
 * Appends one pair to a named path, creating the path when the row does not
 * have it yet. A pair already present is left alone rather than duplicated.
 *
 * @param {{ name: string|null, pairs: object[] }[]} groups
 * @param {{ group: string|null, key: string, value: string }} triple
 */
export const appendTagTriple = (groups, triple) => {
  const index = groups.findIndex((group) => (group.name ?? null) === (triple.group ?? null));
  const pair = { key: triple.key, value: triple.value };
  if (index === -1) return [...groups, { name: triple.group ?? null, pairs: [pair] }];

  const target = groups[index];
  if (target.pairs.some((p) => p.key === pair.key && p.value === pair.value)) return groups;
  const next = [...groups];
  next[index] = { ...target, pairs: [...target.pairs, pair] };
  return next;
};

/**
 * @param {Array|null} wire `[{ unit_id, factor, is_base }]`
 * @returns {{ unitId: number, factor: number, isBase: boolean }[]}
 */
export const unitsFromWire = (wire) =>
  (safeParseJSON(wire, []) || []).map((unit) => ({
    unitId: Number(unit?.unit_id),
    factor: Number(unit?.factor ?? 1),
    isBase: !!unit?.is_base,
  }));

/** @param {{ unitId: number, factor: number, isBase: boolean }[]} units */
export const unitsToWire = (units) =>
  units.map((unit) => ({ unit_id: Number(unit.unitId), factor: Number(unit.factor), is_base: !!unit.isBase }));

/**
 * Marks one unit as the base. A base is always factor 1 by definition — it is
 * the unit every other factor converts into — so switching base rewrites the
 * factors nobody would want left behind.
 */
export const setBaseUnit = (units, unitId) =>
  units.map((unit) => ({
    ...unit,
    isBase: unit.unitId === unitId,
    factor: unit.unitId === unitId ? 1 : unit.factor,
  }));

export const addUnit = (units, unitId) => {
  if (units.some((unit) => unit.unitId === unitId)) return units;
  const isFirst = units.length === 0;
  return [...units, { unitId, factor: 1, isBase: isFirst }];
};

export const removeUnit = (units, unitId) => units.filter((unit) => unit.unitId !== unitId);

export const setUnitFactor = (units, unitId, factor) =>
  units.map((unit) => (unit.unitId === unitId ? { ...unit, factor } : unit));

/**
 * One API question into the flat shape every cell reads. Booleans arrive from
 * MySQL as 0/1, so they are coerced here and nowhere else.
 * @param {object} raw
 */
export const normalizeRow = (raw) => ({
  id: raw.rowId ?? raw.questionId,
  questionId: raw.questionId,
  kpiKey: kpiKeyFromTitle(raw.title),
  financialYearId: raw.financialYearId == null ? "" : String(raw.financialYearId),
  title: raw.title ?? "",
  moduleId: raw.moduleId == null ? "" : String(raw.moduleId),
  type: raw.questionType ?? "quantitative",
  frequency: raw.frequency ?? "EVERY_FY",
  aggregation: raw.aggregation_method ?? "",
  units: unitsFromWire(raw.supportedUnits),
  tagGroups: tagGroupsFromWire(raw.tags),
  tagShape: tagShapeOfWire(raw.tags),
  evidences: stringList(raw.requiredEvidences),
  kpiTypes: kpiTypesFromWire(raw.kpiType),
  defaultDescription: raw.defaultDescription ?? "",
  yearDescription: raw.yearDescription ?? "",
  category: raw.category ?? "",
  applicableCheck: !!raw.applicableCheck,
  applicableNote: !!raw.applicableNote,
  trigerCheck: !!raw.trigerCheck,
  isReadOnly: Number(raw.isReadOnly) === 1,
  details: jsonList(raw.details),
  industryIds: stringList(raw.industryIds),
  frameworkIds: stringList(raw.frameworkIds),
  sectionMapping: raw.frameworkSectionMapping ?? "",
  formula: raw.formula ?? "",
  isFormulaBased: !!raw.isFormulaBased,
  variables: stringList(raw.variables),
  variableUnits: safeParseJSON(raw.variableUnits, {}) || {},
  kpiStatus: raw.kpiStatus || "DRAFT",
  active: !!raw.status,
  updatedAt: raw.updatedAt ?? null,
  factors: [],
});

/**
 * A blank row seeded from the ghost row, inheriting the grid's context. The
 * module comes from the filter above the grid when one is selected, which is
 * what makes a new row land in the module the user is already looking at.
 *
 * @param {string} title
 * @param {{ moduleId?: string }} [context]
 */
export const newDraftRow = (title, { moduleId = "" } = {}) => ({
  id: nextDraftId(),
  questionId: null,
  financialYearId: "",
  kpiKey: "",
  title,
  moduleId: moduleId == null ? "" : String(moduleId),
  type: "quantitative",
  frequency: "EVERY_FY",
  aggregation: "SUM",
  units: [],
  tagGroups: [],
  tagShape: "object",
  evidences: [],
  kpiTypes: [],
  defaultDescription: "",
  yearDescription: "",
  category: "",
  applicableCheck: true,
  applicableNote: true,
  trigerCheck: false,
  isReadOnly: false,
  details: [],
  industryIds: [],
  frameworkIds: [],
  sectionMapping: "",
  formula: "",
  variables: [],
  variableUnits: {},
  kpiStatus: "DRAFT",
  active: true,
  updatedAt: null,
  factors: [],
});

export const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = a || [];
    const right = b || [];
    if (left.length !== right.length) return false;
    const structured = left.some((v) => v && typeof v === "object");
    if (structured) return JSON.stringify(left) === JSON.stringify(right);
    const rightSorted = [...right].sort();
    return [...left].sort().every((v, i) => v === rightSorted[i]);
  }
  if (typeof a === "boolean" || typeof b === "boolean") return !!a === !!b;
  return String(a ?? "") === String(b ?? "");
};

/**
 * Next patch for a row after one cell changes. A cell edited back to its saved
 * value drops out of the patch, so an empty patch always means a clean row.
 *
 * Tags and units are compared in their wire shape: both are edited as nested
 * objects, and a unit factor typed back as `"2"` is the same stored row as the
 * `2` that was loaded.
 *
 * A new question type clears the stored table, as the question form does:
 * `details` laid out for one type means nothing to another.
 * @param {object} row saved row
 * @param {object} patch current unsaved edits
 * @param {string} field
 * @param {*} value
 * @returns {object} the next patch
 */
export const patchField = (row, patch, field, value) => {
  const next = { ...patch };
  const unchanged =
    field === "tagGroups"
      ? JSON.stringify(tagGroupsToWire(row.tagGroups || [], "object")) ===
      JSON.stringify(tagGroupsToWire(value || [], "object"))
      : field === "units"
        ? JSON.stringify(unitsToWire(row.units || [])) === JSON.stringify(unitsToWire(value || []))
        : sameValue(row[field], value);
  if (unchanged) delete next[field];
  else next[field] = value;

  if (field === "type") {
    if (sameValue(row.type, value) || !(row.details || []).length) delete next.details;
    else next.details = [];
    const aggregation = supportsAggregation(value) ? row.aggregation || "SUM" : "";
    const current = "aggregation" in next ? next.aggregation : row.aggregation;
    if (!supportsAggregation(value) || !current) {
      if (sameValue(row.aggregation, aggregation)) delete next.aggregation;
      else next.aggregation = aggregation;
    }
  }
  return next;
};

/** @param {object} patch */
export const isPatchEmpty = (patch) => !patch || Object.keys(patch).length === 0;

export const mergeRow = (row, patch) => ({ ...row, ...patch });

/**
 * Validates the whole grid at once, because uniqueness is a grid-level rule:
 * zb_reporting_question_titles.title is UNIQUE, so two rows cannot claim the
 * same title even though neither is individually wrong.
 * @param {{ id: string|number, values: object }[]} entries
 * @returns {Record<string, Record<string, string>>} errors by row id
 */
export const validateRows = (entries) => {
  const claimsByTitle = new Map();
  entries.forEach(({ id, values }) => {
    const key = String(values.title || "").trim().toLowerCase();
    if (!key) return;
    if (!claimsByTitle.has(key)) claimsByTitle.set(key, { kpis: new Set(), kpiYears: new Set(), repeated: false });
    const claims = claimsByTitle.get(key);
    const kpi = values.questionId ?? id;
    const kpiYear = `${kpi}|${values.financialYearId ?? ""}`;
    if (claims.kpiYears.has(kpiYear)) claims.repeated = true;
    claims.kpis.add(kpi);
    claims.kpiYears.add(kpiYear);
  });
  const titleTaken = (title) => {
    const claims = claimsByTitle.get(title.toLowerCase());
    return claims.repeated || claims.kpis.size > 1;
  };

  const errors = {};
  entries.forEach(({ id, values }) => {
    const rowErrors = {};
    const title = String(values.title || "").trim();
    if (!title) rowErrors.title = "A title is required";
    else if (title.length > 767) rowErrors.title = "Titles cap at 767 characters";
    else if (titleTaken(title))
      rowErrors.title = "Another row already uses this title";
    if (!values.moduleId) rowErrors.moduleId = "Pick a module";
    if (!values.type) rowErrors.type = "Pick a question type";
    if (!values.frequency) rowErrors.frequency = "Pick a frequency";
    if (supportsAggregation(values.type) && !values.aggregation)
      rowErrors.aggregation = "Quantitative KPIs need an aggregation method";

    const unitError = validateUnits(values.units || []);
    if (unitError) rowErrors.units = unitError;

    if (String(values.formula || "").trim() && !(values.variables || []).length)
      rowErrors.variables = "A formula needs its variables";

    if (isDraftId(id) && hasTableSetup(values.type) && !(values.details || []).length)
      rowErrors.details = "Set up the table";

    const factorError = validateFactors(values.factors || []);
    if (factorError) rowErrors.factors = factorError;

    if (Object.keys(rowErrors).length) errors[id] = rowErrors;
  });
  return errors;
};

/**
 * A unit list is a conversion table, so it holds together only if exactly one
 * unit is the base, that base converts one-to-one, and every other factor is a
 * positive multiplier of it.
 * @param {{ unitId: number, factor: number, isBase: boolean }[]} units
 * @returns {string|null}
 */
export const validateUnits = (units) => {
  if (!units.length) return null;
  const bases = units.filter((unit) => unit.isBase);
  if (bases.length === 0) return "One unit must be the base";
  if (bases.length > 1) return "Only one unit can be the base";
  if (Number(bases[0].factor) !== 1) return "The base unit's factor must be 1";
  if (units.some((unit) => !(Number(unit.factor) > 0))) return "Every factor must be greater than zero";
  const ids = units.map((unit) => unit.unitId);
  if (new Set(ids).size !== ids.length) return "A unit is listed twice";
  return null;
};

/**
 * The shape a row's tags are written in. The array form cannot name a group,
 * so an array row that has gained a named group (a fixed tag column) is
 * written as an object; its unnamed paths become group1, group2….
 * @param {{ tagGroups: object[], tagShape: "object"|"array" }} values
 */
export const tagShapeFor = (values) =>
  values.tagShape === "array" && (values.tagGroups || []).some((group) => group.name != null)
    ? "object"
    : values.tagShape;

const flag = (value) => (value ? 1 : 0);

/** `[field, wire key, to wire]` for the fields buildSavePayload sends only when touched. */
const TOUCHED_ONLY_FIELDS = [
  ["kpiTypes", "kpi_type", (types) => (types.length ? types : null)],
  ["defaultDescription", "default_description", String],
  ["yearDescription", "year_description", String],
  ["category", "category", (category) => category || null],
  ["applicableCheck", "applicable_check", flag],
  ["applicableNote", "applicable_note", flag],
  ["trigerCheck", "triger_check", flag],
  ["isReadOnly", "is_read_only", (readOnly) => (readOnly ? 1 : null)],
  ["details", "question_details", (details) => details],
];

/**
 * Saved KPIs whose KPI type is being edited. Their existing answers keep the
 * emission and energy rows they were priced with until they are saved again.
 * @param {object[]} rows
 * @param {Record<string, object>} patches
 * @returns {object[]}
 */
export const savedRowsWithKpiTypeEdit = (rows, patches) =>
  rows.filter((row) => !isDraftId(row.id) && "kpiTypes" in (patches[row.id] || {}));

/**
 * Body for POST saveReportingQuestion. Only changed and new rows are sent;
 * `question_id` is present for an existing row so the backend renames its
 * title rather than creating a second one.
 *
 * `kpi_status` is deliberately absent: the grid does not own publish state,
 * so sending it would overwrite whatever set it.
 *
 * Each row carries its own financial year, so a save can span the years the
 * grid is showing; the request-level one covers rows that have none yet.
 *
 * The fields in TOUCHED_ONLY_FIELDS go only with new rows and rows where that
 * field was edited, so saving another column never rewrites them — kpi_type
 * picks the emission and energy handlers, and `details` is the table answers
 * are filed against.
 *
 * @param {{ rows: object[], patches: object, moduleId: number, financialYearId: number }} args
 */
export const buildSavePayload = ({ rows, patches, moduleId, financialYearId }) => ({
  module_id: moduleId ? Number(moduleId) : null,
  financial_year_id: financialYearId ? Number(financialYearId) : null,
  questions: rows
    .filter((row) => isDraftId(row.id) || !isPatchEmpty(patches[row.id]))
    .map((row) => {
      const v = mergeRow(row, patches[row.id]);
      const formula = String(v.formula).trim();
      const keepsStoredFlag = !!row.isFormulaBased && !("formula" in (patches[row.id] || {}));
      const question = {
        question_title: String(v.title).trim(),
        module_id: Number(v.moduleId),
        question_type: v.type,
        frequency: v.frequency,
        aggregation_method: supportsAggregation(v.type) ? v.aggregation : "SUM",
        supported_units: unitsToWire(v.units),
        tags: tagGroupsToWire(v.tagGroups, tagShapeFor(v)),
        required_evidences: v.evidences,
        industry_ids: v.industryIds.map(Number),
        map_framework_ids: v.frameworkIds.map(Number),
        framework_section_mapping: v.sectionMapping,
        is_formula_based: formula || keepsStoredFlag ? 1 : 0,
        formula: formula || null,
        variables_json: v.variables,
        variable_units_json: Object.fromEntries(
          Object.entries(v.variableUnits || {}).filter(([variable]) => v.variables.includes(variable)),
        ),
        kpi_status: v.kpiStatus,
        status: v.active ? 1 : 0,
      };
      TOUCHED_ONLY_FIELDS.forEach(([field, wireKey, toWire]) => {
        if (isDraftId(row.id) || field in (patches[row.id] || {})) question[wireKey] = toWire(v[field]);
      });
      if (row.financialYearId) question.financial_year_id = Number(row.financialYearId);
      if (!isDraftId(row.id)) question.question_id = row.questionId;
      return question;
    }),
});

/**
 * @typedef {"DIRECT"|"FUEL_ENERGY"} CalculationMethod
 * @typedef {"GAS_MASS"|"CO2E"} EfBasis
 */

/**
 * One emission factor as the grid edits it. Numeric columns stay as the text
 * typed into their inputs, "" meaning not set, until factorsToWire converts them.
 *
 * @typedef {object} GridFactor
 * @property {number|null} id null until the factor is saved
 * @property {string} scope
 * @property {string} countryCode
 * @property {number|""} unitId
 * @property {string} unit the unit master's symbol for unitId, which tells the math whether it is a mass
 * @property {string} value kg CO2e per unit; derived when the factor has a gas breakdown
 * @property {string} databaseId
 * @property {string} databaseName
 * @property {CalculationMethod} method
 * @property {EfBasis} basis
 * @property {string} density kg per unit, FUEL_ENERGY on a unit that is not a mass only
 * @property {string} ncv TJ/Gg, FUEL_ENERGY only
 * @property {string} gwpSource
 * @property {string} efCo2
 * @property {string} efCh4
 * @property {string} efN2o
 * @property {string} gwpCo2
 * @property {string} gwpCh4
 * @property {string} gwpN2o
 */

/**
 * kg CO2e per unit each gas adds, null for a gas the factor does not carry.
 *
 * @typedef {{ co2: number|null, ch4: number|null, n2o: number|null }} GasContributions
 */

/**
 * Factors as the grid edits them. `riu_ghg_emission_factors` stores both
 * `unit_id` and the printed `unit`, and the two agree on every stored row, so
 * only the id is sent back and the backend derives the name; the name is kept
 * here because a mass unit changes the fuel-energy math.
 *
 * @param {object[]} wire rows as getEmissionFactors returns them
 * @returns {GridFactor[]}
 */
export const factorsFromWire = (wire) =>
  (wire || []).map((row) => ({
    id: row.id,
    scope: row.scope,
    countryCode: row.country_code,
    unitId: Number(row.unit_id),
    unit: row.unit || "",
    value: String(row.emission_factor ?? ""),
    databaseId: row.ghg_database_id == null ? "" : String(row.ghg_database_id),
    databaseName: row.ghg_database_name || row.ghgDatabase?.name || "",
    method: row.calculation_method || "DIRECT",
    basis: row.ef_basis || "GAS_MASS",
    density: decimalText(row.density),
    ncv: decimalText(row.ncv),
    gwpSource: row.gwp_source || "",
    ...Object.fromEntries(
      FACTOR_GASES.flatMap(({ gas, ef, gwp }) => [
        [ef, decimalText(row[`ef_${gas}`])],
        [gwp, decimalText(row[`gwp_${gas}`])],
      ]),
    ),
  }));

/** @param {number|string|null|undefined} value @returns {string} */
const decimalText = (value) => (value == null ? "" : String(value));

const KG_PER_GG = 1000000;
const FACTOR_DECIMALS = 10;
const KG_PER_MASS_UNIT = Object.freeze({ g: 0.001, kg: 1, t: 1000, tonne: 1000, tonnes: 1000 });

/** @param {string|number|null|undefined} text @returns {number|null} */
const optionalNumber = (text) => (String(text ?? "").trim() === "" ? null : Number(text));

/**
 * kg in one unit when it is a mass unit, the same table the backend prices with.
 *
 * @param {string|null|undefined} unitName the unit master's `unit` symbol
 * @returns {number|null} null for a unit that is not a mass
 */
export const kgPerMassUnit = (unitName) => KG_PER_MASS_UNIT[String(unitName ?? "").trim().toLowerCase()] ?? null;

/**
 * Whether a factor takes a density: fuel energy on a unit that is not a mass.
 * On a mass unit the unit itself is the kg, so there is no density at all.
 *
 * @param {GridFactor} factor
 * @returns {boolean}
 */
export const needsDensity = (factor) => factor.method === "FUEL_ENERGY" && kgPerMassUnit(factor.unit) == null;

/**
 * Whether a factor is built gas by gas. Such a factor's value is calculated
 * from its gas columns rather than typed.
 *
 * @param {GridFactor} factor
 * @returns {boolean}
 */
export const hasGasBreakdown = (factor) => FACTOR_GASES.some(({ ef }) => optionalNumber(factor[ef]) != null);

/**
 * kg CO2e per unit contributed by each gas, null for a gas the factor does not
 * carry. The same arithmetic the backend stores as emission_factor.
 *
 * @param {GridFactor} factor
 * @returns {GasContributions}
 */
export const factorGasContributions = (factor) => {
  const activity =
    factor.method === "FUEL_ENERGY"
      ? ((kgPerMassUnit(factor.unit) ?? optionalNumber(factor.density) ?? 0) * (optionalNumber(factor.ncv) ?? 0)) / KG_PER_GG
      : 1;
  return Object.fromEntries(
    FACTOR_GASES.map(({ gas, ef, gwp }) => {
      const efValue = optionalNumber(factor[ef]);
      const weight = factor.basis === "CO2E" ? 1 : optionalNumber(factor[gwp]) ?? 0;
      return [gas, efValue == null ? null : Number((activity * efValue * weight).toFixed(FACTOR_DECIMALS))];
    }),
  );
};

/**
 * kg CO2e per unit for a gas-wise factor: the sum of its gas contributions.
 *
 * @param {GridFactor} factor
 * @returns {number}
 */
export const deriveFactorValue = (factor) =>
  Number(
    Object.values(factorGasContributions(factor))
      .reduce((sum, value) => sum + (value ?? 0), 0)
      .toFixed(FACTOR_DECIMALS),
  );

/**
 * Factors grouped by the KPI they price, keeping the order the endpoint
 * returned them in — it sorts by `COALESCE(priority_order, 999)`, so the first
 * factor for a unit is the one that would be applied and the client repeats no
 * ranking logic.
 *
 * @param {object[]} wire
 * @returns {Record<string, object[]>}
 */
export const factorsByKpiFromWire = (wire) => {
  const byKpi = {};
  (wire || []).forEach((row) => {
    const key = String(row.kpi_id);
    if (!byKpi[key]) byKpi[key] = [];
    byKpi[key].push(row);
  });
  Object.keys(byKpi).forEach((key) => {
    byKpi[key] = factorsFromWire(byKpi[key]);
  });
  return byKpi;
};

/**
 * Factors in the shape saveEmissionFactors accepts. NCV is sent only for
 * FUEL_ENERGY and density only when the unit is not a mass, because the
 * backend rejects them anywhere else, and GWPs only with a gas factor, so a
 * single-value factor carries none.
 *
 * @param {GridFactor[]} factors
 * @returns {object[]}
 */
export const factorsToWire = (factors) =>
  factors.map((factor) => {
    const fuelEnergy = factor.method === "FUEL_ENERGY";
    const gasWise = hasGasBreakdown(factor);
    return {
      id: factor.id ?? undefined,
      scope: factor.scope,
      country_code: factor.countryCode,
      unit_id: Number(factor.unitId),
      emission_factor: gasWise ? deriveFactorValue(factor) : Number(factor.value),
      ghg_database_id: factor.databaseId === "" ? null : Number(factor.databaseId),
      calculation_method: factor.method || "DIRECT",
      ef_basis: factor.basis || "GAS_MASS",
      density: needsDensity(factor) ? optionalNumber(factor.density) : null,
      ncv: fuelEnergy ? optionalNumber(factor.ncv) : null,
      gwp_source: gasWise ? String(factor.gwpSource || "").trim() || null : null,
      ...Object.fromEntries(
        FACTOR_GASES.flatMap(({ gas, ef, gwp }) => [
          [`ef_${gas}`, optionalNumber(factor[ef])],
          [`gwp_${gas}`, gasWise ? optionalNumber(factor[gwp]) : null],
        ]),
      ),
    };
  });

const EMPTY_BREAKDOWN = {
  method: "DIRECT",
  basis: "GAS_MASS",
  density: "",
  ncv: "",
  gwpSource: "",
  ...Object.fromEntries(FACTOR_GASES.flatMap(({ ef, gwp }) => [[ef, ""], [gwp, ""]])),
};

/**
 * @param {{ unitId?: number|"", unit?: string, scope?: string, countryCode?: string, databaseId?: string }} [defaults]
 * @returns {GridFactor}
 */
export const newFactor = ({ unitId = "", unit = "", scope = "Scope 1", countryCode = "IN", databaseId = "" } = {}) => ({
  id: null,
  scope,
  countryCode,
  unitId,
  unit,
  value: "",
  databaseId,
  databaseName: "",
  ...EMPTY_BREAKDOWN,
});

/**
 * A factor has to price something specific, so every part of its address is
 * required. Duplicates are not rejected — the stored data already holds them,
 * and the reader breaks the tie on database priority.
 *
 * @param {GridFactor[]} factors
 * @returns {string|null}
 */
export const validateFactors = (factors) => {
  for (let index = 0; index < factors.length; index += 1) {
    const factor = factors[index];
    const where = factors.length > 1 ? ` (row ${index + 1})` : "";
    if (!factor.scope) return `Every factor needs a scope${where}`;
    if (!String(factor.countryCode || "").trim()) return `Every factor needs a country${where}`;
    if (!factor.unitId) return `Every factor needs a unit${where}`;
    if (!factor.databaseId) return `Every factor needs a source database${where}`;
    const breakdownError = validateBreakdown(factor);
    if (breakdownError) return `${breakdownError}${where}`;
    if (hasGasBreakdown(factor)) continue;
    const value = Number(factor.value);
    if (factor.value === "" || !Number.isFinite(value) || value < 0)
      return `Every factor needs a value of zero or more${where}`;
  }
  return null;
};

/** @param {string} text @returns {boolean} */
const isBadNumber = (text) => {
  const value = optionalNumber(text);
  return value != null && (!Number.isFinite(value) || value < 0);
};

/**
 * The same rules the backend applies to a gas-wise factor, so a bad one is
 * caught on the row instead of failing the whole save.
 *
 * @param {GridFactor} factor
 * @returns {string|null}
 */
const validateBreakdown = (factor) => {
  const fuelEnergy = factor.method === "FUEL_ENERGY";
  const typedDensity = needsDensity(factor);
  const numberFields = [
    ...(typedDensity ? [["density", "Density"]] : []),
    ...(fuelEnergy ? [["ncv", "NCV"]] : []),
    ...FACTOR_GASES.flatMap(({ label, ef, gwp }) => [[ef, `${label} factor`], [gwp, `${label} GWP`]]),
  ];
  const bad = numberFields.find(([key]) => isBadNumber(factor[key]));
  if (bad) return `${bad[1]} must be a number of zero or more`;

  if (fuelEnergy) {
    if (typedDensity && !(optionalNumber(factor.density) > 0)) return "Fuel energy needs a density above zero";
    if (!(optionalNumber(factor.ncv) > 0)) return "Fuel energy needs an NCV above zero";
    if (!hasGasBreakdown(factor)) return "Fuel energy needs at least one gas factor";
  }
  if (factor.basis !== "CO2E") {
    const missingGwp = FACTOR_GASES.find(({ ef, gwp }) => optionalNumber(factor[ef]) != null && optionalNumber(factor[gwp]) == null);
    if (missingGwp) return `${missingGwp.label} needs a GWP`;
  }
  return null;
};

/**
 * Applies one factor-cell action to a KPI's factor list.
 *
 * A new factor inherits the scope, country and source of the one before it —
 * a KPI being priced in a second unit almost always sits in the same scope and
 * market. A KPI's first factor takes them from `action.defaults` (the last
 * factor edited anywhere in the grid), so pricing a run of KPIs only needs the
 * value typed. Its unit defaults to the KPI's base unit.
 *
 * `prune` drops new factors nothing was typed into — the editor sends it on
 * close, so opening a cell and walking away leaves nothing to block save. A
 * factor with only its breakdown started is kept, for validation to point at.
 *
 * Clearing the last gas factor also clears the GWPs and GWP source, so the
 * factor goes back to a plain single value.
 *
 * @param {GridFactor[]} factors
 * @param {{ unitId: number, isBase: boolean }[]} units the KPI's supported units
 * @param {{ type: "add"|"remove"|"change"|"prune", index?: number, patch?: Partial<GridFactor>, defaults?: Partial<GridFactor> }} action
 * @param {(unitId: number|"") => string} unitNameOf the unit master's symbol for a unit id, "" when unknown
 * @returns {GridFactor[]} the next factor list
 */
export const applyFactorAction = (factors, units, action, unitNameOf) => {
  if (action.type === "add") {
    const last = factors[factors.length - 1] || action.defaults || {};
    const base = units.find((unit) => unit.isBase) || units[0];
    return [
      ...factors,
      newFactor({
        unitId: base?.unitId ?? "",
        unit: unitNameOf(base?.unitId ?? ""),
        scope: last.scope || "Scope 1",
        countryCode: last.countryCode || "IN",
        databaseId: last.databaseId || "",
      }),
    ];
  }
  if (action.type === "remove") {
    return factors.filter((factor, index) => index !== action.index);
  }
  if (action.type === "prune") {
    const kept = factors.filter((factor) => factor.id != null || !isUntyped(factor));
    return kept.length === factors.length ? factors : kept;
  }
  return factors.map((factor, index) => {
    if (index !== action.index) return factor;
    const changed = { ...factor, ...action.patch };
    return withDerivedValue(factor, { ...changed, unit: unitNameOf(changed.unitId) });
  });
};

const CLEARED_GWPS = {
  gwpSource: "",
  ...Object.fromEntries(FACTOR_GASES.map(({ gwp }) => [gwp, ""])),
};

/**
 * A changed factor with its value recalculated when it is built gas by gas,
 * and its GWPs cleared when the change removed its last gas factor.
 *
 * @param {GridFactor} before the factor as it was
 * @param {GridFactor} factor the factor with the change applied
 * @returns {GridFactor}
 */
const withDerivedValue = (before, factor) => {
  if (hasGasBreakdown(factor)) return { ...factor, value: String(deriveFactorValue(factor)) };
  return hasGasBreakdown(before) ? { ...factor, ...CLEARED_GWPS } : factor;
};

/**
 * Whether nothing was typed into a factor: no value and its breakdown still at
 * the defaults a new factor starts with.
 *
 * @param {GridFactor} factor
 * @returns {boolean}
 */
const isUntyped = (factor) =>
  String(factor.value ?? "").trim() === "" &&
  Object.entries(EMPTY_BREAKDOWN).every(([key, empty]) => String(factor[key] ?? "").trim() === empty);

/**
 * KPI ids from a saveReportingQuestion response, keyed by title.
 *
 * This is what lets a brand-new KPI carry emission factors in the same save.
 * A factor needs a `kpi_id`, which is the title row's primary key and does not
 * exist until the KPI is written — but the save response returns each row's
 * `question_id` alongside its `title`, and
 * `zb_reporting_question_titles.title` is UNIQUE, so title is a safe key to
 * map back through. No id has to be guessed and no extra round trip is needed.
 *
 * @param {object[]} responseRows `data` from the KPI save
 * @returns {Map<string, number>} lower-cased title to question_id
 */
export const kpiIdsByTitle = (responseRows) => {
  const map = new Map();
  (responseRows || []).forEach((row) => {
    const title = String(row?.title ?? "").trim().toLowerCase();
    if (title && row?.question_id != null) map.set(title, Number(row.question_id));
  });
  return map;
};

/**
 * Body for POST saveEmissionFactors: one entry per KPI whose factor list
 * changed.
 *
 * Saved rows are addressed by their own `questionId`. A draft row has none
 * yet, so `resolveKpiId` looks its id up from the KPI save response — which is
 * why the KPI call runs first. A draft whose id cannot be resolved is skipped
 * rather than posted against a guess, and reported by the caller.
 *
 * @param {{ rows: object[], patches: object, resolveKpiId?: (row: object) => number|null }} args
 */
export const buildFactorPayload = ({ rows, patches, resolveKpiId }) => {
  const itemByKpi = new Map();
  const unresolved = [];
  const conflicting = [];

  rows.forEach((row) => {
    const draft = isDraftId(row.id);
    const patch = patches[row.id];
    const factors = draft ? row.factors || [] : patch?.factors;

    const changed = draft ? factors.length > 0 : !!patch && "factors" in patch;
    if (!changed) return;

    const kpiId = draft ? resolveKpiId?.(row) ?? null : row.questionId;
    if (kpiId == null) {
      unresolved.push(row.title);
      return;
    }
    const item = { kpi_id: kpiId, factors: factorsToWire(factors) };
    const earlier = itemByKpi.get(kpiId);
    if (!earlier) {
      itemByKpi.set(kpiId, item);
      return;
    }
    if (JSON.stringify(earlier.factors) !== JSON.stringify(item.factors) && !conflicting.includes(row.title)) {
      conflicting.push(row.title);
    }
  });

  return { items: [...itemByKpi.values()], unresolved, conflicting };
};

export const toggleInList = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
